import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  folderService,
  type Folder,
  type FolderTreeNode,
} from "@/services/folderService";
import { aiAssistantService } from "@/services/aiAssistantService";
import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Folder as FolderIcon,
  FolderOpen,
  ArrowLeft,
  Trash2,
  Pencil,
  MoveRight,
  Archive,
  ArchiveRestore,
  Calendar,
  User as UserIcon,
  Lock,
  LockOpen,
} from "lucide-react";
import { EditFolderModal } from "@/components/ai-assistant/EditFolderModal";
import { CreateFolderModal } from "@/components/ai-assistant/CreateFolderModal";
import { useToast } from "@/hooks/use-toast";
import { AIOutputView } from "@/components/ai-assistant/AIOutputView";
import { FolderAssignDropdown } from "@/components/ai-assistant/FolderAssignDropdown";
import { profileService } from "@/services/profileService";
import { RegenerateModal } from "@/components/ai-assistant/RegenerateModal";

export default function FoldersPage() {
  const { organization, user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const initialFolder = searchParams.get("folder");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    initialFolder,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState<any | null>(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [showMobileDetailView, setShowMobileDetailView] = useState(false);

  const orgId = organization?.id || "";

  const { data: treeData, isLoading: isTreeLoading } = useQuery({
    queryKey: ["folderTree", orgId, includeArchived],
    queryFn: async () => {
      if (!orgId) return [] as FolderTreeNode[];
      const { data, error } = await folderService.getFolderTree(
        orgId,
        includeArchived,
      );
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  const selectedNode = useMemo(
    () => treeData?.find((f) => f.id === selectedFolderId) || null,
    [treeData, selectedFolderId],
  );

  // Load assistant instance and base assistant for Improve flow
  const instanceIdForSelected =
    selectedOutput?.ai_assistant_instance_id || null;
  const assistantIdForSelected = selectedOutput?.ai_assistant_id || null;

  const { data: instanceData } = useQuery({
    queryKey: ["foldersSelectedInstance", instanceIdForSelected],
    queryFn: async () => {
      if (!instanceIdForSelected) return null;
      const res = await aiAssistantService.getAssistantInstance(
        instanceIdForSelected,
      );
      if (res.error) throw res.error;
      return res.data;
    },
    enabled: !!instanceIdForSelected,
  });

  const { data: baseAssistant } = useQuery({
    queryKey: ["foldersSelectedAssistant", assistantIdForSelected],
    queryFn: async () => {
      if (!assistantIdForSelected) return null;
      const res = await aiAssistantService.getSystemAssistant(
        assistantIdForSelected,
      );
      if (res.error) throw res.error;
      return res.data;
    },
    enabled: !!assistantIdForSelected,
  });

  const handleRegenerate = async (feedback?: string) => {
    if (!selectedOutput) return;
    try {
      setIsImproving(true);
      const organizationId = orgId || (selectedOutput as any).organization_id;
      const assistantId = (selectedOutput as any).ai_assistant_id;
      const instanceId = (selectedOutput as any).ai_assistant_instance_id;

      const assistantConfig: any = {
        baseInstructions:
          (instanceData as any)?.custom_instructions ||
          (baseAssistant as any)?.base_instructions ||
          "",
        customInstructions: (instanceData as any)?.custom_instructions || "",
        zodSchema:
          (instanceData as any)?.custom_zod_schema ||
          (baseAssistant as any)?.zod_schema ||
          {},
        formFields:
          (instanceData as any)?.custom_form_fields ||
          (baseAssistant as any)?.form_fields ||
          [],
        model: (baseAssistant as any)?.model,
        temperature: (baseAssistant as any)?.temperature,
        max_output_tokens: (baseAssistant as any)?.max_output_tokens,
        web_search_enabled: (baseAssistant as any)?.web_search_enabled,
        generation_mode: (baseAssistant as any)?.generation_mode || "object",
      };

      const url = `${(window as any).ENV?.VITE_SUPABASE_URL || (import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`;
      const reqBody = {
        assistantId,
        instanceId,
        inputData: (selectedOutput as any).input_data || {},
        organizationId,
        userId: user?.id,
        assistantConfig,
        isRegeneration: true,
        previousOutput: (selectedOutput as any).structured_output,
        userFeedback: feedback,
        outputId: (selectedOutput as any).id,
      } as any;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(window as any).ENV?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(reqBody),
      });

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("Failed to open stream");
      let buffer = "";
      let textMode = false;
      let textBuffer = "";

      // Wipe current content so streaming starts from empty (like Generate tab)
      setSelectedOutput((prev: any) =>
        prev ? { ...prev, structured_output: "" } : prev,
      );

      // Prime meta for mode
      // We can't await the first chunk separately; infer from events
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "meta" && data.mode === "text") textMode = true;
            if (data.type === "text-delta") {
              const delta: string = data.data || "";
              textBuffer += delta;
              setSelectedOutput((prev: any) =>
                prev ? { ...prev, structured_output: textBuffer } : prev,
              );
            }
            if (data.type === "partial") {
              const part = data.data;
              setSelectedOutput((prev: any) =>
                prev ? { ...prev, structured_output: part } : prev,
              );
            }
            if (data.type === "complete") {
              const finalData = data.data;
              setSelectedOutput((prev: any) =>
                prev ? { ...prev, structured_output: finalData } : prev,
              );
            }
          } catch (_) {}
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["folderOutputs"] });
      await queryClient.invalidateQueries({
        queryKey: ["organizationOutputs"],
      });
      toast({ title: "Improved", description: "Output has been regenerated." });
    } catch (e: any) {
      toast({
        title: "Regeneration failed",
        description: e?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsImproving(false);
    }
  };

  // Sync selected folder with URL changes (e.g., when switching from sidebar)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const folder = sp.get("folder");
    setSelectedFolderId(folder);
    setSelectedOutput(null);
  }, [location.search]);

  // Rendering schema is not needed here; let renderer infer
  const renderingSchema = useMemo(() => null, [selectedOutput]);

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await folderService.renameFolder(id, name);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      toast({ title: "Folder renamed" });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to rename folder",
        description: err.message,
        variant: "destructive",
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: string; archive: boolean }) => {
      const { error } = await folderService.archiveFolder(id, archive);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      toast({ title: "Folder state updated" });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to update folder",
        description: err.message,
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await folderService.deleteFolder(id);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (selectedFolderId) setSelectedFolderId(null);
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      toast({ title: "Folder deleted" });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to delete folder",
        description: err.message,
        variant: "destructive",
      }),
  });

  // Outputs in selected folder
  const { data: outputsData, isLoading: isOutputsLoading } = useQuery({
    queryKey: ["folderOutputs", orgId, selectedFolderId],
    queryFn: async () => {
      if (!orgId) return [] as any[];
      const { data, error } = await aiAssistantService.getOrganizationOutputs(
        orgId,
        100,
        0,
        { folderId: selectedFolderId || undefined },
      );
      if (error) throw error;
      // Enrich with user names if present
      const outputs = data || [];
      const userIds = Array.from(
        new Set(outputs.map((o: any) => o.user_id).filter(Boolean)),
      );
      if (userIds.length > 0) {
        const prof = await profileService.getProfilesByIds(userIds as string[]);
        const idToName = new Map<string, string>();
        if (prof.success && prof.data) {
          prof.data.forEach((p) => {
            const full =
              p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ");
            if (p.id && full) idToName.set(p.id, full);
          });
        }
        return outputs.map((o: any) => ({
          ...o,
          user_name: o.user_id ? idToName.get(o.user_id) || "" : "",
        }));
      }
      return outputs;
    },
    enabled: !!orgId,
  });

  const moveOutputMutation = useMutation({
    mutationFn: async ({
      outputId,
      folderId,
    }: {
      outputId: string;
      folderId: string | null;
    }) => {
      const { error } = await aiAssistantService.moveOutputToFolder(
        outputId,
        folderId,
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderOutputs"] });
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      toast({ title: "Output moved" });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to move output",
        description: err.message,
        variant: "destructive",
      }),
  });

  const [editingFolder, setEditingFolder] = useState<Folder | null>(
    null as any,
  );

  const FolderRow = ({ node }: { node: FolderTreeNode }) => {
    const isSelected = node.id === selectedFolderId;
    const colorStyle = node.folder_color
      ? ({ color: node.folder_color } as React.CSSProperties)
      : undefined;
    return (
      <div
        className={`flex items-center justify-between px-2 py-1.5 cursor-pointer rounded ${isSelected ? "bg-slate-100" : "hover:bg-slate-50"}`}
        style={{ paddingLeft: 8 + node.depth * 12 }}
        onClick={() => {
          setSelectedFolderId(node.id);
          navigate(`/folders?folder=${node.id}`);
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isSelected ? (
            <FolderOpen size={16} style={colorStyle} />
          ) : (
            <FolderIcon size={16} style={colorStyle} />
          )}
          <div className="truncate">
            <div className="text-sm font-medium truncate">{node.name}</div>
            <div className="text-xs text-muted-foreground">
              {node.output_count} items
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setEditingFolder(node as any);
            }}
          >
            <Pencil size={14} />
          </Button>
          {(organization?.team_members_count || 0) > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                archiveMutation.mutate({
                  id: node.id,
                  archive: !node.is_archived,
                });
              }}
            >
              {node.is_archived ? (
                <ArchiveRestore size={14} />
              ) : (
                <Archive size={14} />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Delete this folder?"))
                deleteMutation.mutate(node.id);
            }}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="pb-0 h-full">
        <div className="grid grid-cols-12 gap-0 h-full">
          {/* Left: Folder contents list (hidden on mobile when detail is shown) */}
          <div
            className={`col-span-12 md:col-span-4 bg-white border-x border-y rounded-none overflow-hidden flex flex-col h-full ${
              showMobileDetailView ? "hidden md:flex" : "flex"
            }`}
          >
            <div className="p-4 border-b">
              {selectedNode ? (
                <div className="space-y-1">
                  <div className="text-xs text-gray-500 truncate">{`Folders / ${selectedNode.path.join(" / ")}`}</div>
                  <div className="flex items-center gap-2 justify-between">
                    <button
                      className="flex items-center gap-2 min-w-0"
                      onClick={() => setEditingFolder(selectedNode as any)}
                    >
                      <FolderOpen
                        size={20}
                        style={{
                          color: selectedNode.folder_color || undefined,
                        }}
                      />
                      <h2 className="text-2xl font-semibold truncate">
                        {selectedNode.name}
                      </h2>
                    </button>
                    {(organization?.team_members_count || 0) > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          folderService
                            .updateFolder(selectedNode.id, {
                              is_private: !selectedNode.is_private,
                            } as any)
                            .then(() =>
                              queryClient.invalidateQueries({
                                queryKey: ["folderTree"],
                              }),
                            )
                        }
                        title={
                          selectedNode.is_private
                            ? "Make public"
                            : "Make private"
                        }
                        className="h-6 w-6 p-0"
                      >
                        {selectedNode.is_private ? (
                          <Lock size={16} />
                        ) : (
                          <LockOpen size={16} />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">Folders</div>
                  <div className="flex items-center gap-2">
                    <FolderIcon size={20} />
                    <h2 className="text-2xl font-semibold">Select a folder</h2>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto divide-y">
              {isOutputsLoading ? (
                <div className="p-4 text-sm text-gray-500">
                  Loading contents...
                </div>
              ) : (outputsData || []).length === 0 ? (
                <div className="p-4 text-sm text-gray-500">
                  No outputs in this folder yet.
                </div>
              ) : (
                (outputsData || []).map((out: any) => {
                  const friendlyDate = new Date(
                    out.created_at,
                  ).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                  return (
                    <div
                      key={out.id}
                      className={`p-4 cursor-pointer ${selectedOutput?.id === out.id ? "bg-gray-50" : "hover:bg-gray-50"}`}
                      onClick={() => {
                        setSelectedOutput(out);
                        setShowMobileDetailView(true);
                      }}
                    >
                      <div className="text-sm font-medium truncate">
                        {out.output_title || out.id}
                      </div>
                      <div className="text-xs text-gray-500 truncate flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={12} />
                          {friendlyDate}
                        </span>
                        {!!out.user_id && (
                          <span className="inline-flex items-center gap-1">
                            <UserIcon size={12} />
                            {(out as any).user_name || "User"}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Output preview (full width on mobile when shown) */}
          <div
            className={`col-span-12 md:col-span-8 bg-gray-50 rounded-none border-x border-y overflow-auto h-full ${
              showMobileDetailView
                ? "fixed inset-0 z-50 md:relative md:inset-auto md:z-auto"
                : "hidden md:block"
            }`}
          >
            {/* Mobile back button */}
            {showMobileDetailView && (
              <div className="md:hidden sticky top-0 z-10 bg-white border-b p-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowMobileDetailView(false)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Folders
                </Button>
              </div>
            )}
            {!selectedFolderId ? (
              <div className="text-sm text-muted-foreground">
                Choose a folder on the left to see its contents.
              </div>
            ) : isOutputsLoading ? (
              <div className="text-sm text-muted-foreground">
                Loading contents...
              </div>
            ) : (
              <div className="space-y-2">
                {!selectedOutput ? (
                  <div className="p-6 text-center text-sm text-gray-500">
                    Select an output on the left to view it.
                  </div>
                ) : (
                  <AIOutputView
                    key={(selectedOutput as any)?.id}
                    output={selectedOutput}
                    isSaved={false}
                    onToggleSave={() => {}}
                    renderingSchema={renderingSchema as any}
                    isGenerating={isImproving}
                    // Keep editor active if DB flag is set
                    // AIOutputView already reads output.is_edit_mode, so nothing else needed here
                    onImprove={() => setShowRegenerateModal(true)}
                    onTitleSave={async (newTitle) => {
                      await aiAssistantService.updateAIOutputTitle(
                        (selectedOutput as any).id,
                        newTitle,
                      );
                      setSelectedOutput((prev) =>
                        prev ? { ...prev, output_title: newTitle } : prev,
                      );
                    }}
                    extraActionsRight={
                      <FolderAssignDropdown
                        outputId={(selectedOutput as any).id}
                        currentFolderId={
                          (selectedOutput as any).folder_id || null
                        }
                        onChange={(folderId) => {
                          setSelectedOutput((prev) =>
                            prev
                              ? { ...prev, folder_id: folderId as any }
                              : prev,
                          );
                        }}
                      />
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {editingFolder && (
          <EditFolderModal
            folder={editingFolder as any}
            open={!!editingFolder}
            onOpenChange={(open) => {
              if (!open) setEditingFolder(null);
            }}
          />
        )}
        {isCreating && (
          <CreateFolderModal
            parentFolderId={selectedFolderId || null}
            open={isCreating}
            onOpenChange={(open) => setIsCreating(open)}
          />
        )}
        <RegenerateModal
          isOpen={showRegenerateModal}
          onClose={() => setShowRegenerateModal(false)}
          onRegenerate={(fb?: string) => handleRegenerate(fb)}
          isGenerating={isImproving}
        />
      </div>
    </MainLayout>
  );
}
